/**
 * Webtech HR Engine™ - SuperAdmin Users Management Dashboard
 * Phase 3: Gestion globale des utilisateurs
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Users,
  UserCheck,
  UserX,
  UserCog,
  Shield,
  Building2,
  Search,
  Filter,
  MoreVertical,
  Eye,
  KeyRound,
  LogOut,
  UserMinus,
  UserPlus,
  Activity,
  Clock,
  Calendar,
  Crown,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Mail,
  Briefcase,
  History,
  Trash2
} from 'lucide-react';

// Formatage date relative
const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Hier';
  if (diff < 7) return `Il y a ${diff} jours`;
  if (diff < 30) return `Il y a ${Math.floor(diff / 7)} semaines`;
  if (diff < 365) return `Il y a ${Math.floor(diff / 30)} mois`;
  return date.toLocaleDateString('fr-FR');
};

// Status Badge
function UserStatusBadge({ status }) {
  const statusConfig = {
    active: { label: 'Actif', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    inactive: { label: 'Inactif', class: 'bg-slate-100 text-slate-700', icon: UserX },
    suspended: { label: 'Suspendu', class: 'bg-red-100 text-red-700', icon: XCircle }
  };

  const config = statusConfig[status] || statusConfig.active;
  const Icon = config.icon;

  return (
    <Badge className={`${config.class} font-medium`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
}

// Role Badge
function RoleBadge({ role }) {
  const roleConfig = {
    Admin: { class: 'bg-purple-100 text-purple-700', icon: Shield },
    Manager: { class: 'bg-blue-100 text-blue-700', icon: Briefcase },
    Employé: { class: 'bg-slate-100 text-slate-700', icon: Users }
  };

  const config = roleConfig[role] || roleConfig.Employé;
  const Icon = config.icon;

  return (
    <Badge className={`${config.class} font-medium`}>
      <Icon className="w-3 h-3 mr-1" />
      {role}
    </Badge>
  );
}

// Dialog pour changer le rôle
function ChangeRoleDialog({ isOpen, onClose, user, onSuccess }) {
  const [role, setRole] = useState(user?.role || '');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) setRole(user.role);
  }, [user]);

  const handleSubmit = async () => {
    if (role === user?.role) {
      toast.info('Aucun changement');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/superadmin/users/${user.id}`, { role, reason });
      toast.success('Rôle modifié avec succès');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5 text-purple-500" />
            Changer le rôle
          </DialogTitle>
          <DialogDescription>
            {user?.firstName} {user?.lastName} ({user?.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Nouveau rôle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
                <SelectItem value="Employé">Employé</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Raison (optionnel)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motif du changement..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || role === user?.role}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Modifier le rôle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog pour réinitialiser le mot de passe
function ResetPasswordDialog({ isOpen, onClose, user, onSuccess }) {
  const [newPassword, setNewPassword] = useState('');
  const [notifyUser, setNotifyUser] = useState(true);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let pwd = '';
    for (let i = 0; i < 12; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(pwd);
  };

  const handleSubmit = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/superadmin/users/${user.id}/reset-password`, {
        newPassword,
        notifyUser,
        reason
      });
      toast.success('Mot de passe réinitialisé');
      onSuccess();
      onClose();
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-amber-500" />
            Réinitialiser le mot de passe
          </DialogTitle>
          <DialogDescription>
            {user?.firstName} {user?.lastName} ({user?.email})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Nouveau mot de passe</Label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nouveau mot de passe"
              />
              <Button variant="outline" onClick={generatePassword}>
                Générer
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="notifyUser"
              checked={notifyUser}
              onChange={(e) => setNotifyUser(e.target.checked)}
              className="rounded border-slate-300"
            />
            <Label htmlFor="notifyUser" className="text-sm font-normal">
              Notifier l'utilisateur par email
            </Label>
          </div>

          <div className="space-y-2">
            <Label>Raison (optionnel)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motif de la réinitialisation..."
              rows={2}
            />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            L'utilisateur devra changer son mot de passe à la prochaine connexion.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading || !newPassword}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Réinitialiser
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog pour voir l'historique d'activité
function ActivityHistoryDialog({ isOpen, onClose, user }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && user) {
      fetchActivities();
    }
  }, [isOpen, user]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/superadmin/users/${user.id}/activity?limit=50`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-500" />
            Historique d'activité
          </DialogTitle>
          <DialogDescription>
            {user?.firstName} {user?.lastName} ({user?.email})
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune activité enregistrée</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activities.map((activity, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                    <Activity className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{activity.action}</p>
                    <p className="text-xs text-slate-500">{activity.description || activity.category}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatRelativeDate(activity.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Confirmation de déconnexion forcée
function ForceLogoutDialog({ isOpen, onClose, user, onSuccess }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/superadmin/users/${user.id}/force-logout`, { reason });
      toast.success('Utilisateur déconnecté');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la déconnexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <LogOut className="w-5 h-5 text-red-500" />
            Forcer la déconnexion
          </AlertDialogTitle>
          <AlertDialogDescription>
            Cela invalidera toutes les sessions actives de {user?.firstName} {user?.lastName}.
            L'utilisateur devra se reconnecter.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <Label>Raison *</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motif de la déconnexion forcée..."
            rows={2}
            className="mt-2"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleSubmit}
            disabled={loading || !reason.trim()}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Déconnecter
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Composant principal
export default function SuperAdminUsersDashboard() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companies, setCompanies] = useState([]);
  
  // Dialogs
  const [changeRoleDialog, setChangeRoleDialog] = useState({ open: false, user: null });
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null });
  const [activityDialog, setActivityDialog] = useState({ open: false, user: null });
  const [forceLogoutDialog, setForceLogoutDialog] = useState({ open: false, user: null });
  const [deactivateConfirm, setDeactivateConfirm] = useState({ open: false, user: null });
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, user: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes, companiesRes] = await Promise.all([
        api.get('/superadmin/users'),
        api.get('/superadmin/users/stats/overview'),
        api.get('/superadmin/companies')
      ]);
      
      setUsers(usersRes.data.users || []);
      setStats(statsRes.data);
      setCompanies(companiesRes.data.companies || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter users — only show 1 default/main Admin per company (not all client-created users).
  // Strategy: group by companyId, keep the oldest Admin (= the founder/default account) per company.
  // SuperAdmin users (no companyId) are always shown.
  const usersToDisplay = (() => {
    const adminsByCompany = new Map();
    const others = [];
    for (const u of users) {
      if (u.role === 'SuperAdmin' || !u.companyId) {
        others.push(u);
        continue;
      }
      if (u.role !== 'Admin') continue;  // skip client-created Managers/Employés
      const existing = adminsByCompany.get(u.companyId);
      const created = new Date(u.createdAt || 0).getTime();
      const existingCreated = existing ? new Date(existing.createdAt || 0).getTime() : Infinity;
      if (!existing || created < existingCreated) {
        adminsByCompany.set(u.companyId, u);
      }
    }
    return [...others, ...adminsByCompany.values()];
  })();

  const filteredUsers = usersToDisplay.filter(user => {
    const matchesSearch = !searchTerm || 
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    const matchesCompany = companyFilter === 'all' || user.companyId === companyFilter;
    
    return matchesSearch && matchesRole && matchesStatus && matchesCompany;
  });

  // Handle deactivate
  const handleDeactivate = async (user) => {
    try {
      if (user.status === 'inactive' || user.status === 'suspended') {
        await api.post(`/superadmin/users/${user.id}/reactivate`);
        toast.success('Utilisateur réactivé');
      } else {
        await api.post(`/superadmin/users/${user.id}/deactivate`, {}, {
          params: { reason: 'Désactivation par SuperAdmin' }
        });
        toast.success('Utilisateur désactivé');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
    setDeactivateConfirm({ open: false, user: null });
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Utilisateurs</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.total || 0}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Admins</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.byRole?.Admin || 0}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Actifs (7j)</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.activity?.active7d || 0}</p>
                <p className="text-xs text-slate-500">{stats?.activity?.activityRate7d || 0}%</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl">
                <UserCheck className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Jamais connectés</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.activity?.neverLoggedIn || 0}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Suspendus</p>
                <p className="text-2xl font-bold text-slate-900">{stats?.byStatus?.suspended || 0}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-red-500 to-red-600 rounded-xl">
                <UserX className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Répartition par rôle</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { role: 'Admin', count: stats?.byRole?.Admin || 0, color: 'bg-purple-500' },
              { role: 'Manager', count: stats?.byRole?.Manager || 0, color: 'bg-blue-500' },
              { role: 'Employé', count: stats?.byRole?.Employé || 0, color: 'bg-slate-500' }
            ].map(item => {
              const percentage = stats?.total ? Math.round((item.count / stats.total) * 100) : 0;
              return (
                <div key={item.role} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{item.role}</span>
                    <span className="font-medium">{item.count} ({percentage}%)</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom, email ou entreprise..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[150px]">
                <Shield className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Rôle" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les rôles</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
                <SelectItem value="Employé">Employé</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="inactive">Inactif</SelectItem>
                <SelectItem value="suspended">Suspendu</SelectItem>
              </SelectContent>
            </Select>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Entreprise" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Utilisateurs ({filteredUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucun utilisateur trouvé</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map(user => (
                <div 
                  key={user.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-webtech-blue to-blue-700 rounded-full flex items-center justify-center text-white font-bold">
                      {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">
                          {user.firstName} {user.lastName}
                        </h3>
                        <RoleBadge role={user.role} />
                        <UserStatusBadge status={user.status || 'active'} />
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {user.companyName || 'N/A'}
                        </span>
                        {user.subscriptionPlan === 'pro' && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">
                            <Crown className="w-3 h-3 mr-1" />
                            PRO
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Dernière connexion: {formatRelativeDate(user.lastLoginAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Inscrit: {formatRelativeDate(user.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setChangeRoleDialog({ open: true, user })}>
                        <UserCog className="w-4 h-4 mr-2 text-purple-500" />
                        Changer le rôle
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setResetPasswordDialog({ open: true, user })}>
                        <KeyRound className="w-4 h-4 mr-2 text-amber-500" />
                        Réinitialiser le mot de passe
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setActivityDialog({ open: true, user })}>
                        <History className="w-4 h-4 mr-2 text-blue-500" />
                        Voir l'historique
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setForceLogoutDialog({ open: true, user })}>
                        <LogOut className="w-4 h-4 mr-2 text-orange-500" />
                        Forcer la déconnexion
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => setDeactivateConfirm({ open: true, user })}
                        className={user.status === 'active' ? 'text-red-600' : 'text-emerald-600'}
                      >
                        {user.status === 'active' || !user.status ? (
                          <>
                            <UserMinus className="w-4 h-4 mr-2" />
                            Désactiver
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Réactiver
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => setDeleteUserDialog({ open: true, user })}
                        className="text-red-600"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer définitivement
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ChangeRoleDialog
        isOpen={changeRoleDialog.open}
        onClose={() => setChangeRoleDialog({ open: false, user: null })}
        user={changeRoleDialog.user}
        onSuccess={fetchData}
      />

      <ResetPasswordDialog
        isOpen={resetPasswordDialog.open}
        onClose={() => setResetPasswordDialog({ open: false, user: null })}
        user={resetPasswordDialog.user}
        onSuccess={fetchData}
      />

      <ActivityHistoryDialog
        isOpen={activityDialog.open}
        onClose={() => setActivityDialog({ open: false, user: null })}
        user={activityDialog.user}
      />

      <ForceLogoutDialog
        isOpen={forceLogoutDialog.open}
        onClose={() => setForceLogoutDialog({ open: false, user: null })}
        user={forceLogoutDialog.user}
        onSuccess={fetchData}
      />

      <AlertDialog open={deactivateConfirm.open} onOpenChange={(open) => !open && setDeactivateConfirm({ open: false, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deactivateConfirm.user?.status === 'active' || !deactivateConfirm.user?.status
                ? 'Désactiver cet utilisateur ?'
                : 'Réactiver cet utilisateur ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateConfirm.user?.status === 'active' || !deactivateConfirm.user?.status
                ? `${deactivateConfirm.user?.firstName} ${deactivateConfirm.user?.lastName} ne pourra plus se connecter.`
                : `${deactivateConfirm.user?.firstName} ${deactivateConfirm.user?.lastName} pourra à nouveau se connecter.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeactivate(deactivateConfirm.user)}
              className={deactivateConfirm.user?.status === 'active' || !deactivateConfirm.user?.status
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-emerald-600 hover:bg-emerald-700'}
            >
              {deactivateConfirm.user?.status === 'active' || !deactivateConfirm.user?.status
                ? 'Désactiver'
                : 'Réactiver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Dialog */}
      <AlertDialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog({ open, user: deleteUserDialog.user })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Supprimer l'utilisateur
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-2">
                Êtes-vous sûr de vouloir supprimer définitivement <strong>{deleteUserDialog.user?.firstName} {deleteUserDialog.user?.lastName}</strong> ({deleteUserDialog.user?.email}) ?
              </p>
              <p className="text-red-600 font-medium">
                ⚠️ Cette action est irréversible. Toutes les données de l'utilisateur seront perdues.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                setDeleteLoading(true);
                try {
                  await api.delete(`/superadmin/users/${deleteUserDialog.user?.id}`);
                  toast.success('Utilisateur supprimé');
                  setDeleteUserDialog({ open: false, user: null });
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
                } finally {
                  setDeleteLoading(false);
                }
              }}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Suppression...' : 'Supprimer définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
